import { TagType } from "@/api/enums";

import { generatedApi } from "./generatedApi";

export const enhancedInstitutionApi = generatedApi.enhanceEndpoints({
  addTagTypes: [
    TagType.SprengConnectApi_InstitutionResponse,
    TagType.SprengConnectApi_InstitutionProfileSettingsResponse,
    TagType.SprengConnectApi_InstitutionUsersResponse,
    TagType.SprengConnectApi_UserProfileResponse,
  ],
  endpoints: {
    getServiceApi2BackOfficeGetInstitutionsPageByNameAndId: {
      providesTags: (result) =>
        result?.data
          ? result.data.map(() => ({
              type: TagType.SprengConnectApi_InstitutionResponse,
              id: `after-${result.pagination?.after}-before-${result.pagination?.before}`,
            }))
          : [TagType.SprengConnectApi_InstitutionResponse],
    },
    getServiceApi2BackOfficeGetAllInstitutions: {
      providesTags: (result) =>
        result
          ? result.map((institution) => ({
              type: TagType.SprengConnectApi_InstitutionResponse,
              id: institution.id,
            }))
          : [TagType.SprengConnectApi_InstitutionResponse],
    },
    postServiceApi2BackOfficeCreateInstitution: {
      invalidatesTags: [TagType.SprengConnectApi_InstitutionResponse],
    },
    putServiceApi2BackOfficeUpdateInstitution: {
      invalidatesTags: (result) =>
        result
          ? [{ type: TagType.SprengConnectApi_InstitutionResponse, id: result.data?.id }]
          : [TagType.SprengConnectApi_InstitutionResponse],
    },
    deleteServiceApi2BackOfficeDeleteInstitution: {
      invalidatesTags: [TagType.SprengConnectApi_InstitutionResponse],
    },
    getServiceApiInstitutionProfileSettings: {
      providesTags: [{ type: TagType.SprengConnectApi_InstitutionProfileSettingsResponse, id: "profile-settings" }],
    },
    putServiceApiInstitutionProfileSettings: {
      invalidatesTags: [
        { type: TagType.SprengConnectApi_InstitutionProfileSettingsResponse, id: "profile-settings" },
        { type: TagType.SprengConnectApi_UserProfileResponse, id: "profile" },
      ],
    },
    getServiceApiInstitutionUsers: {
      providesTags: [TagType.SprengConnectApi_InstitutionUsersResponse],
    },
    postServiceApiInstitutionCreateUser: {
      invalidatesTags: [TagType.SprengConnectApi_InstitutionUsersResponse],
    },
    putServiceApiInstitutionUpdateUser: {
      invalidatesTags: [TagType.SprengConnectApi_InstitutionUsersResponse],
    },
    deleteServiceApiInstitutionDeleteUser: {
      invalidatesTags: [TagType.SprengConnectApi_InstitutionUsersResponse],
    },
  },
});

export const enhancedUserApi = generatedApi.enhanceEndpoints({
  addTagTypes: [
    TagType.SprengConnectApi_UserResponse,
    TagType.SprengConnectApi_UserProfileSettingsResponse,
    TagType.SprengConnectApi_UserProfileResponse,
  ],
  endpoints: {
    getServiceApi2BackOfficeGetUsersByInstitution: {
      providesTags: (result) =>
        result?.value
          ? result.value.map((user) => ({
              type: TagType.SprengConnectApi_UserResponse,
              id: user.institution_id ?? undefined,
            }))
          : [TagType.SprengConnectApi_UserResponse],
    },
    putServiceApi2BackOfficeAssignUserToInstitution: {
      invalidatesTags: (result) =>
        result
          ? [{ type: TagType.SprengConnectApi_UserResponse, id: result.data?.id ?? undefined }]
          : [TagType.SprengConnectApi_UserResponse],
    },
    postServiceApi2BackOfficeCreateUser: {
      invalidatesTags: [TagType.SprengConnectApi_UserResponse],
    },
    postServiceApi2BackOfficeCreateUserWithInstitution: {
      invalidatesTags: (result) => {
        const tags: (
          | TagType.SprengConnectApi_UserResponse
          | { type: TagType.SprengConnectApi_UserResponse; id: number }
        )[] = [TagType.SprengConnectApi_UserResponse];

        if (result?.data?.institution_id) {
          tags.push({
            type: TagType.SprengConnectApi_UserResponse,
            id: result.data.institution_id,
          });
        }

        return tags;
      },
    },
    deleteServiceApi2BackOfficeDeleteUser: {
      invalidatesTags: (result) => {
        const tags: (
          | TagType.SprengConnectApi_UserResponse
          | { type: TagType.SprengConnectApi_UserResponse; id: number }
        )[] = [TagType.SprengConnectApi_UserResponse];

        if (result?.data?.institution_id) {
          tags.push({
            type: TagType.SprengConnectApi_UserResponse,
            id: result.data.institution_id,
          });
        }

        return tags;
      },
    },
    putServiceApi2BackOfficeUpdateUser: {
      invalidatesTags: (result) => {
        const tags: (
          | TagType.SprengConnectApi_UserResponse
          | { type: TagType.SprengConnectApi_UserResponse; id: number }
        )[] = [TagType.SprengConnectApi_UserResponse];

        if (result?.institution_id) {
          tags.push({
            type: TagType.SprengConnectApi_UserResponse,
            id: result.institution_id,
          });
        }

        return tags;
      },
    },
    getServiceApiUserProfileSettings: {
      providesTags: [{ type: TagType.SprengConnectApi_UserProfileSettingsResponse, id: "profile-settings" }],
    },
    putServiceApiUserProfileSettings: {
      invalidatesTags: [
        { type: TagType.SprengConnectApi_UserProfileSettingsResponse, id: "profile-settings" },
        { type: TagType.SprengConnectApi_UserProfileResponse, id: "profile" },
      ],
    },
    getServiceApiUserProfile: {
      providesTags: [{ type: TagType.SprengConnectApi_UserProfileResponse, id: "profile" }],
    },
  },
});

export const enhancedSubscriptionApi = generatedApi.enhanceEndpoints({
  addTagTypes: [TagType.SprengConnectApi_SubscriptionResponse, TagType.SprengConnectApi_IndexResponse],
  endpoints: {
    getServiceApi2BackOfficeGetSubscriptions: {
      providesTags: (result) =>
        result
          ? result.map(({ id }) => ({ type: TagType.SprengConnectApi_SubscriptionResponse, id }))
          : [TagType.SprengConnectApi_SubscriptionResponse],
    },
    getServiceApi2BackOfficeGetAllIndexFeatures: {
      providesTags: (result) =>
        result?.value
          ? result.value.map(({ index, feature_name }) => ({
              type: TagType.SprengConnectApi_SubscriptionResponse,
              index,
              feature_name,
            }))
          : [TagType.SprengConnectApi_SubscriptionResponse],
    },
    putServiceApi2BackOfficeUpdateSubscription: {
      invalidatesTags: (result) => {
        const tags: { type: TagType.SprengConnectApi_SubscriptionResponse; id?: number }[] = result
          ? [{ type: TagType.SprengConnectApi_SubscriptionResponse, id: result.data?.id }]
          : [{ type: TagType.SprengConnectApi_SubscriptionResponse }];
        return [...tags, TagType.SprengConnectApi_IndexResponse];
      },
    },
    postServiceApi2BackOfficeCreateSubscription: {
      invalidatesTags: [TagType.SprengConnectApi_SubscriptionResponse],
    },
    deleteServiceApi2BackOfficeDeleteSubscription: {
      invalidatesTags: [TagType.SprengConnectApi_SubscriptionResponse],
    },
    getServiceApi2BackOfficeGetInstitutionsBySubscription: {
      providesTags: (result) =>
        result?.value
          ? result.value.map(({ institution_id }) => ({
              type: TagType.SprengConnectApi_SubscriptionResponse,
              institution_id,
            }))
          : [TagType.SprengConnectApi_SubscriptionResponse],
    },
  },
});

export const enhancedIndexApi = generatedApi.enhanceEndpoints({
  addTagTypes: [TagType.SprengConnectApi_IndexResponse],
  endpoints: {
    getServiceApi2BackOfficeGetAllIndexes: {
      providesTags: (result) =>
        result
          ? result.map(({ id }) => ({ type: TagType.SprengConnectApi_IndexResponse, id }))
          : [TagType.SprengConnectApi_IndexResponse],
    },
    getServiceApi2BackOfficeGetAllIndexFeatures: {
      providesTags: (result) =>
        result?.value
          ? result.value.map(({ index, feature_name }) => ({
              type: TagType.SprengConnectApi_IndexResponse,
              index,
              feature_name,
            }))
          : [TagType.SprengConnectApi_IndexResponse],
    },
    putServiceApi2BackOfficeUpdateIndexById: {
      invalidatesTags: (result) =>
        result
          ? [{ type: TagType.SprengConnectApi_IndexResponse, id: result.data?.id }]
          : [TagType.SprengConnectApi_IndexResponse],
    },
    postServiceApi2BackOfficeAddIndex: {
      invalidatesTags: [TagType.SprengConnectApi_IndexResponse],
    },
    putServiceApi2BackOfficeAddFeatureToIndex: {
      invalidatesTags: (result) =>
        result
          ? [{ type: TagType.SprengConnectApi_IndexResponse, id: result.data?.id }]
          : [TagType.SprengConnectApi_IndexResponse],
    },
    deleteServiceApi2BackOfficeRemoveFeatureFromIndex: {
      invalidatesTags: [TagType.SprengConnectApi_IndexResponse],
    },
    postServiceApi2BackOfficeAddDocument: {
      invalidatesTags: [TagType.SprengConnectApi_IndexResponse],
    },
    deleteServiceApi2BackOfficeRemoveDocument: {
      invalidatesTags: [TagType.SprengConnectApi_IndexResponse],
    },
  },
});

export const enhancedFeatureApi = generatedApi.enhanceEndpoints({
  addTagTypes: [TagType.SprengConnectApi_FeatureResponse],
  endpoints: {
    getServiceApi2BackOfficeGetAllFeatures: {
      providesTags: (result) =>
        result?.value
          ? result.value.map(({ id }) => ({ type: TagType.SprengConnectApi_FeatureResponse, id }))
          : [TagType.SprengConnectApi_FeatureResponse],
    },
  },
});

export const enhancedValuationPackageApi = generatedApi.enhanceEndpoints({
  addTagTypes: [TagType.ValuationPackage_Valuations, TagType.ValuationPackage_Images, TagType.ValuationPackage_Status],
  endpoints: {
    getServiceApiValuationPackageValuations: {
      providesTags: [TagType.ValuationPackage_Valuations],
    },
    getServiceApiValuationPackageStatus: {
      providesTags: [TagType.ValuationPackage_Status],
    },
    postServiceApiValuationPackageValuation: {
      invalidatesTags: [TagType.ValuationPackage_Valuations],
    },
    postServiceApiValuationPackageValuationByItemIdCopy: {
      invalidatesTags: [TagType.ValuationPackage_Valuations],
    },
    postServiceApiValuationPackageValuationByItemIdValuate: {
      invalidatesTags: [TagType.ValuationPackage_Valuations, TagType.ValuationPackage_Status],
    },
    deleteServiceApiValuationPackageValuationByItemId: {
      invalidatesTags: [TagType.ValuationPackage_Valuations],
    },
    putServiceApiValuationPackageValuationByItemId: {
      invalidatesTags: [TagType.ValuationPackage_Valuations],
    },
    getServiceApiValuationPackageValuationByValuationPackageItemIdImages: {
      providesTags: (_result, _error, arg) => [
        { type: TagType.ValuationPackage_Images, id: arg.valuationPackageItemId },
      ],
    },
    postServiceApiValuationPackageValuationByValuationPackageItemIdImage: {
      invalidatesTags: (__result, _error, arg) => [
        { type: TagType.ValuationPackage_Images, id: arg.valuationPackageItemId },
      ],
    },
    deleteServiceApiValuationPackageValuationImageById: {
      invalidatesTags: [TagType.ValuationPackage_Images],
    },
    putServiceApiValuationPackageValuationByValuationPackageItemIdImages: {
      invalidatesTags: (_result, _error, arg) => [
        { type: TagType.ValuationPackage_Images, id: arg.valuationPackageItemId },
      ],
    },
    postServiceApiValuationPackageValuationByItemIdReportPdf: {
      query: (queryArg) => ({
        url: `/service/api/valuation_package/valuation/${queryArg.itemId}/report/pdf`,
        method: "POST",
        body: queryArg.body,
        responseHandler: async (response: Response): Promise<Blob> => {
          if (!response.ok) throw new Error("Failed to create PDF report");
          return response.blob();
        },
      }),
    },
    getServiceApiValuationPackageValuationByItemIdReportPdf: {
      query: (queryArg) => ({
        url: `/service/api/valuation_package/valuation/${queryArg.itemId}/report/pdf`,
        method: "GET",
        responseHandler: async (response: Response): Promise<Blob> => {
          if (!response.ok) throw new Error("Failed to download PDF report");
          return response.blob();
        },
      }),
    },
    postServiceApiValuationPackageValuationByItemIdReportDocx: {
      query: (queryArg) => ({
        url: `/service/api/valuation_package/valuation/${queryArg.itemId}/report/docx`,
        method: "POST",
        body: queryArg.body,
        responseHandler: async (response: Response): Promise<Blob> => {
          if (!response.ok) throw new Error("Failed to create DOCX report");
          return response.blob();
        },
      }),
    },
    getServiceApiValuationPackageValuationByItemIdReportDocx: {
      query: (queryArg) => ({
        url: `/service/api/valuation_package/valuation/${queryArg.itemId}/report/docx`,
        method: "GET",
        responseHandler: async (response: Response): Promise<Blob> => {
          if (!response.ok) throw new Error("Failed to download DOCX report");
          return response.blob();
        },
      }),
    },
    getServiceApiValuationReportReport: {
      query: (queryArg) => ({
        url: `/service/api/valuation_report/report`,
        method: "GET",
        headers: {
          "X-Session-Token": queryArg["X-Session-Token"],
        },
        responseHandler: async (response: Response): Promise<Blob> => {
          if (!response.ok) {
            const text = await response.text();
            console.error("PDF download failed:", response.status, text);
            throw new Error(`Failed to download PDF: `);
          }
          return response.blob();
        },
      }),
    },
    postServiceApiValuationReportReport: {
      query: (queryArg) => ({
        url: `/service/api/valuation_report/report`,
        method: "POST",
        headers: {
          "X-Session-Token": queryArg["X-Session-Token"],
        },
        body: queryArg.body,
        responseHandler: async (response: Response): Promise<Blob> => {
          if (!response.ok) {
            const text = await response.text();
            console.error("PDF creation failed:", response.status, text);
            throw new Error(`Failed to create PDF: `);
          }
          return response.blob();
        },
      }),
    },
  },
});

export const {
  useGetServiceApi2BackOfficeGetInstitutionsPageByNameAndIdQuery,
  usePostServiceApi2BackOfficeCreateInstitutionMutation,
  usePutServiceApi2BackOfficeUpdateInstitutionMutation,
  useDeleteServiceApi2BackOfficeDeleteInstitutionMutation,
  useGetServiceApi2BackOfficeGetSubscriptionsQuery,
  useGetServiceApi2BackOfficeGetAllIndexFeaturesQuery,
  usePutServiceApi2BackOfficeUpdateSubscriptionMutation,
  usePostServiceApi2BackOfficeCreateSubscriptionMutation,
  useDeleteServiceApi2BackOfficeDeleteSubscriptionMutation,
  useGetServiceApi2BackOfficeGetInstitutionsBySubscriptionQuery,
  useGetServiceApi2BackOfficeGetAllIndexesQuery,
  usePostServiceApi2BackOfficeAddIndexMutation,
  usePutServiceApi2BackOfficeUpdateIndexByIdMutation,
  useGetServiceApi2BackOfficeGetAllFeaturesQuery,
  useDeleteServiceApi2BackOfficeRemoveFeatureFromIndexMutation,
  usePutServiceApi2BackOfficeAddFeatureToIndexMutation,
  usePostServiceApi2BackOfficeAddDocumentMutation,
  useDeleteServiceApi2BackOfficeRemoveDocumentMutation,
  useGetServiceApi2BackOfficeGetAllInstitutionsQuery,
  useGetServiceApiInstitutionProfileSettingsQuery,
  usePutServiceApiInstitutionProfileSettingsMutation,
  useGetServiceApiInstitutionUsersQuery,
  usePostServiceApiInstitutionCreateUserMutation,
  usePutServiceApiInstitutionUpdateUserMutation,
  useDeleteServiceApiInstitutionDeleteUserMutation,
} = enhancedInstitutionApi;

export const {
  useGetServiceApi2BackOfficeGetUsersByInstitutionQuery,
  usePutServiceApi2BackOfficeAssignUserToInstitutionMutation,
  useDeleteServiceApi2BackOfficeDeleteUserMutation,
  usePostServiceApi2BackOfficeCreateUserMutation,
  usePostServiceApi2BackOfficeCreateUserWithInstitutionMutation,
  usePutServiceApi2BackOfficeUpdateUserMutation,
  useGetServiceApiUserProfileSettingsQuery,
  usePutServiceApiUserProfileSettingsMutation,
  useGetServiceApiUserProfileQuery,
} = enhancedUserApi;

export const {
  useLazyGetServiceApiValuationReportAvmValuateQuery,
  useLazyGetServiceApiValuationReportProsawebValuateQuery,
  useLazyGetServiceApiValuationReportSoneValuateQuery,
  usePutServiceApiValuationReportRealEstateMutation,
  useLazyGetServiceApiValuationPackageValuationByItemIdQuery,
  useDeleteServiceApiValuationPackageValuationByItemIdMutation,
  usePostServiceApiValuationPackageValuationByItemIdValuateMutation,
  usePostServiceApiValuationPackageValuationByItemIdReportPdfMutation,
  useLazyGetServiceApiValuationPackageValuationByItemIdReportPdfQuery,
  usePostServiceApiValuationPackageValuationByItemIdReportDocxMutation,
  useLazyGetServiceApiValuationPackageValuationByItemIdReportDocxQuery,
  useLazyGetServiceApiValuationReportReportQuery,
  useLazyGetServiceApiValuationPackageValuationByValuationPackageItemIdImagesQuery,
  useGetServiceApiValuationPackageStatusQuery,
  usePostServiceApiValuationPackageValuationByItemIdCopyMutation,
  useLazyGetServiceApiValuationPackageValuationsQuery,
  usePostServiceApiValuationReportReportMutation,
} = enhancedValuationPackageApi;
